import { AlertTriangle, Trash2 } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { ClientByIdResponse } from "../types/clients-api.types";
import { displayClientName } from "../utils/clientsTableHelpers";

export type DeleteEntityVariant = "client" | "organization";

export interface DeleteEntityDialogsProps {
  /**
   * Entity being deleted, in the client-detail shape. An organization is mapped into that same shape
   * (`mapOrganizationDetailToViewClient` / the client-profile endpoint) so both variants share the preview.
   */
  entity: ClientByIdResponse | null;
  variant: DeleteEntityVariant;
  /** `null` closes both dialogs; "first" warns, "final" asks again before the irreversible call. */
  step: "first" | "final" | null;
  deleting: boolean;
  onCancel: () => void;
  onProceed: () => void;
  onConfirm: () => void;
}

const COPY_KEYS = {
  client: {
    confirmTitle: "view_delete_client_confirm_title",
    confirmMessage: "view_delete_client_confirm_message",
    finalTitle: "view_delete_client_final_confirm_title",
    finalMessage: "view_delete_client_final_confirm_message",
    sectionHeading: "view_delete_client_section_heading",
  },
  organization: {
    confirmTitle: "view_delete_organization_confirm_title",
    confirmMessage: "view_delete_organization_confirm_message",
    finalTitle: "view_delete_organization_final_confirm_title",
    finalMessage: "view_delete_organization_final_confirm_message",
    sectionHeading: "view_delete_organization_section_heading",
  },
} as const;

/**
 * Two-step deletion confirmation (warn → confirm again) with a read-only preview of the target. Shared by the
 * client detail drawer and the organization detail page; the variant only swaps the copy.
 */
export function DeleteEntityDialogs({
  entity,
  variant,
  step,
  deleting,
  onCancel,
  onProceed,
  onConfirm,
}: DeleteEntityDialogsProps) {
  const { t } = useTranslations("clients");
  const keys = COPY_KEYS[variant];
  const displayName = entity ? displayClientName(entity) : "—";

  return (
    <>
      <ColocaDialog
        open={step === "first"}
        onOpenChange={(open) => {
          if (!open) onCancel();
        }}
        title={t(keys.confirmTitle)}
        size="default"
        footer={
          <>
            <ColocaButton type="button" variant="ghost" onClick={onCancel} disabled={deleting}>
              {t("view_delete_client_cancel")}
            </ColocaButton>
            <ColocaButton
              type="button"
              variant="destructive"
              onClick={onProceed}
              disabled={deleting}
              icon={Trash2}
            >
              {t("view_delete_client_confirm")}
            </ColocaButton>
          </>
        }
      >
        <DeleteEntityPreview
          entity={entity}
          message={t(keys.confirmMessage, { name: displayName })}
          sectionHeading={t(keys.sectionHeading)}
          registeredLabel={t("view_label_registration_date")}
          tone="warning"
        />
      </ColocaDialog>

      <ColocaDialog
        open={step === "final"}
        onOpenChange={(open) => {
          if (!open) onCancel();
        }}
        title={t(keys.finalTitle)}
        size="default"
        footer={
          <>
            <ColocaButton type="button" variant="ghost" onClick={onCancel} disabled={deleting}>
              {t("view_delete_client_cancel")}
            </ColocaButton>
            <ColocaButton
              type="button"
              variant="destructive"
              onClick={onConfirm}
              disabled={deleting}
              loading={deleting}
              icon={Trash2}
            >
              {t("view_delete_client_final_confirm_button")}
            </ColocaButton>
          </>
        }
      >
        <DeleteEntityPreview
          entity={entity}
          message={t(keys.finalMessage, { name: displayName })}
          sectionHeading={t(keys.sectionHeading)}
          registeredLabel={t("view_label_registration_date")}
          tone="danger"
        />
      </ColocaDialog>
    </>
  );
}

interface DeleteEntityPreviewProps {
  entity: ClientByIdResponse | null;
  message: string;
  sectionHeading: string;
  registeredLabel: string;
  tone: "warning" | "danger";
}

function DeleteEntityPreview({
  entity,
  message,
  sectionHeading,
  registeredLabel,
  tone,
}: DeleteEntityPreviewProps) {
  const { t } = useTranslations("clients");
  if (!entity) return null;

  const alertWrapperClass =
    tone === "danger"
      ? "flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-container p-3"
      : "flex items-start gap-3 rounded-xl border border-warning/30 bg-warning-container p-3";
  const alertIconClass = tone === "danger" ? "h-5 w-5 shrink-0 text-danger" : "h-5 w-5 shrink-0 text-warning";
  const alertTextClass =
    tone === "danger"
      ? "text-sm leading-relaxed text-danger-container-foreground"
      : "text-sm leading-relaxed text-warning-container-foreground";

  const name = displayClientName(entity);
  const initial = name?.trim()?.[0]?.toUpperCase() || "?";

  return (
    <div className="space-y-4">
      <div className={alertWrapperClass}>
        <AlertTriangle className={alertIconClass} />
        <p className={alertTextClass}>{message}</p>
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {sectionHeading}
        </p>
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center gap-3 border-b border-border bg-muted px-4 py-3">
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-danger-container text-sm font-bold text-danger-container-foreground">
              {initial}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{name}</p>
              {entity.email && <p className="truncate text-xs text-muted-foreground">{entity.email}</p>}
            </div>
            <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {entity.type.charAt(0).toUpperCase() + entity.type.slice(1).toLowerCase()}
            </span>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 px-4 py-3 text-xs sm:grid-cols-2">
            {entity.country && (
              <div className="flex min-w-0 flex-col">
                <dt className="font-medium uppercase tracking-wide text-muted-foreground">
                  {t("view_delete_field_country")}
                </dt>
                <dd className="truncate text-foreground">{entity.country}</dd>
              </div>
            )}
            {entity.phone && (
              <div className="flex min-w-0 flex-col">
                <dt className="font-medium uppercase tracking-wide text-muted-foreground">
                  {t("view_delete_field_phone")}
                </dt>
                <dd className="truncate text-foreground">{entity.phone}</dd>
              </div>
            )}
          </dl>

          <div className="border-t border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
            {registeredLabel}: {formatDateTime(entity.created_at, { includeTime: false }) || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}
